import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
  LinearProgress, Checkbox, InputAdornment, FormControlLabel,
} from '@mui/material';
import { Balance, Add, Undo, Search, Warning } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const YEAR = new Date().getFullYear();

const ADJ_TYPE_CFG: Record<string, { label: string; color: string; emoji: string }> = {
  bonus:               { label: 'Bonus Days',          color: '#2e7d32', emoji: '🎁' },
  correction:          { label: 'Correction',          color: '#1565c0', emoji: '✏️' },
  compassionate:       { label: 'Compassionate',       color: '#6a1b9a', emoji: '❤️' },
  admin:               { label: 'Admin Override',      color: '#37474f', emoji: '🔧' },
  deduction:           { label: 'Deduction',           color: '#b71c1c', emoji: '➖' },
  carry_in:            { label: 'Carry-In',            color: '#e65100', emoji: '↩️' },
  encashment_reversal: { label: 'Encashment Reversal', color: '#f57f17', emoji: '↩️' },
  other:               { label: 'Other',               color: '#546e7a', emoji: '📄' },
};

interface AdjRow {
  id: string; employeeId: string; firstName: string; lastName: string; empNo: string;
  leaveTypeId: string; leaveTypeName: string; leaveTypeCode: string;
  year: number; days: number; adjustmentType: string; reason: string; notes?: string;
  effectiveDate: string; createdByName: string; isReversed: boolean; createdAt: string;
}

interface BalRow {
  employeeId: string; firstName: string; lastName: string; empNo: string; deptName: string;
  leaveTypeId: string; leaveTypeName: string; leaveTypeCode: string;
  year: number; openingBalanceDays: number; accruedDays: number; carryOverDays: number;
  adjustedDays: number; usedDays: number; closingBalanceDays: number; adjustmentCount: number;
}

const emptyForm = {
  employeeId: '', leaveTypeId: '', year: YEAR, days: 1,
  adjustmentType: 'bonus', reason: '', notes: '', effectiveDate: new Date().toISOString().slice(0, 10),
};

export default function LeaveAdjustments() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');

  const [tab, setTab]           = useState(0);
  const [adjRows, setAdjRows]   = useState<AdjRow[]>([]);
  const [balRows, setBalRows]   = useState<BalRow[]>([]);
  const [stats, setStats]       = useState<any>(null);
  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);
  const [employees, setEmployees]   = useState<any[]>([]);
  const [year, setYear]         = useState(YEAR);
  const [typeFilter, setTypeFilter] = useState('');
  const [ltFilter, setLtFilter]     = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [search, setSearch]     = useState('');
  const [showReversed, setShowReversed] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Bulk selection for bulk grant
  const [selectedEmpIds, setSelectedEmpIds] = useState<string[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [bulkData, setBulkData] = useState({ leaveTypeId: '', year: YEAR, days: 1, adjustmentType: 'bonus', reason: '', notes: '', effectiveDate: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving]     = useState(false);

  const TABS = isHR ? ['Adjustment Log', 'Balance Admin', 'Dashboard'] : ['My Leave Adjustments'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (typeFilter) params.set('type', typeFilter);
      if (showReversed) params.set('includeReversed', 'true');

      const calls: Promise<any>[] = [apiFetch(`/api/leave-adjustments?${params}`)];
      if (isHR) {
        const bParams = new URLSearchParams({ year: String(year) });
        if (ltFilter) bParams.set('leaveTypeId', ltFilter);
        if (deptFilter) bParams.set('departmentId', deptFilter);
        calls.push(apiFetch(`/api/leave-adjustments/balances?${bParams}`));
        calls.push(apiFetch(`/api/leave-adjustments/stats?year=${year}`));
        calls.push(apiFetch('/api/leave/types'));
        calls.push(apiFetch('/api/employees?limit=500'));
      }

      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setAdjRows(results[0].value ?? []);
      if (results[1] && results[1].status === 'fulfilled') setBalRows((results[1] as any).value ?? []);
      if (results[2] && results[2].status === 'fulfilled') setStats((results[2] as any).value);
      if (results[3] && results[3].status === 'fulfilled') {
        const d = (results[3] as any).value;
        setLeaveTypes(Array.isArray(d) ? d : (d.data ?? []));
      }
      if (results[4] && results[4].status === 'fulfilled') {
        const d = (results[4] as any).value;
        setEmployees(Array.isArray(d) ? d : (d.data ?? []));
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, year, typeFilter, ltFilter, deptFilter, showReversed, isHR]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/leave-adjustments', { method: 'POST', body: JSON.stringify(formData) });
      setFormOpen(false); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const saveBulk = async () => {
    if (!selectedEmpIds.length) return;
    setSaving(true);
    try {
      const result = await apiFetch('/api/leave-adjustments/bulk', {
        method: 'POST',
        body: JSON.stringify({ ...bulkData, employeeIds: selectedEmpIds }),
      });
      const failed = result.filter((r: any) => r.status === 'error');
      if (failed.length) setError(`${failed.length} employees failed: ${failed[0].error}`);
      setBulkOpen(false); setSelectedEmpIds([]); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const reverse = async (id: string) => {
    try {
      await apiFetch(`/api/leave-adjustments/${id}/reverse`, { method: 'PATCH' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const filteredAdj = adjRows.filter(r => {
    if (!search) return true;
    const name = `${r.firstName} ${r.lastName}`.toLowerCase();
    return name.includes(search.toLowerCase()) || r.reason.toLowerCase().includes(search.toLowerCase());
  });

  const filteredBal = balRows.filter(r => {
    if (!search) return true;
    const name = `${r.firstName} ${r.lastName}`.toLowerCase();
    return name.includes(search.toLowerCase()) || r.deptName?.toLowerCase().includes(search.toLowerCase());
  });

  // Unique dept list from balRows
  const deptsSet: Record<string, boolean> = {};
  balRows.forEach(r => { if (r.deptName) deptsSet[r.deptName] = true; });
  const depts = Object.keys(deptsSet).sort();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#C08A2E', width: 48, height: 48 }}><Balance /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Leave Balance Adjustments</Typography>
            <Typography variant="body2" color="text.secondary">
              Grant bonus days, correct balances, compassionate leave, and bulk department grants
            </Typography>
          </Box>
        </Box>
        {isHR && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<Add />} onClick={() => setBulkOpen(true)}>
              Bulk Grant
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setFormData({ ...emptyForm }); setFormOpen(true); }}
              sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#9E6F22' } }}>
              Adjust Balance
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats row */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total Adjustments', value: stats.total,              color: '#37474f' },
            { label: 'Grants',            value: stats.grants,             color: '#2e7d32' },
            { label: 'Deductions',        value: stats.deductions,         color: '#b71c1c' },
            { label: 'Days Granted',      value: `+${parseFloat(stats.totalGranted || '0').toFixed(1)}d`, color: '#2e7d32' },
            { label: 'Days Deducted',     value: `-${parseFloat(stats.totalDeducted || '0').toFixed(1)}d`, color: '#b71c1c' },
            { label: 'Employees Affected', value: stats.employeesAffected, color: '#1565c0' },
          ].map(s => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={s.label}>
              <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <Typography variant="h5" fontWeight={700} color={s.color}>{s.value ?? 0}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {/* Filters bar */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {isHR && (
          <TextField size="small" placeholder="Search employee / reason…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        )}
        <TextField select size="small" label="Year" value={year} onChange={e => setYear(parseInt(e.target.value))} sx={{ width: 90 }}>
          {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        {isHR && tab === 0 && (
          <TextField select size="small" label="Type" value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All Types</MenuItem>
            {Object.entries(ADJ_TYPE_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>)}
          </TextField>
        )}
        {isHR && tab === 1 && (
          <>
            <TextField select size="small" label="Leave Type" value={ltFilter}
              onChange={e => setLtFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Leave Types</MenuItem>
              {leaveTypes.map(lt => <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Department" value={deptFilter}
              onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Departments</MenuItem>
              {depts.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </>
        )}
        {isHR && tab === 0 && (
          <FormControlLabel control={<Checkbox size="small" checked={showReversed} onChange={e => setShowReversed(e.target.checked)} />}
            label={<Typography variant="body2">Show reversed</Typography>} />
        )}
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* ── Adjustment Log ── */}
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {isHR && <TableCell>Employee</TableCell>}
                    <TableCell>Leave Type</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Effective</TableCell>
                    <TableCell>By</TableCell>
                    {isHR && <TableCell>Action</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAdj.length === 0 && (
                    <TableRow><TableCell colSpan={isHR ? 9 : 7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No adjustments for {year}
                    </TableCell></TableRow>
                  )}
                  {filteredAdj.map(r => {
                    const tc = ADJ_TYPE_CFG[r.adjustmentType] ?? ADJ_TYPE_CFG.other;
                    const isGrant = r.days > 0;
                    return (
                      <TableRow key={r.id} hover sx={{ opacity: r.isReversed ? 0.45 : 1 }}>
                        {isHR && (
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{r.firstName} {r.lastName}</Typography>
                            <Typography variant="caption" color="text.secondary">{r.empNo}</Typography>
                          </TableCell>
                        )}
                        <TableCell><Chip size="small" label={r.leaveTypeName} sx={{ fontSize: '0.65rem' }} /></TableCell>
                        <TableCell>{r.year}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}
                            color={isGrant ? '#2e7d32' : '#b71c1c'}>
                            {isGrant ? '+' : ''}{r.days}d
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={`${tc.emoji} ${tc.label}`}
                            sx={{ bgcolor: tc.color + '15', color: tc.color, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{r.reason}</Typography>
                          {r.notes && <Typography variant="caption" color="text.secondary">{r.notes}</Typography>}
                        </TableCell>
                        <TableCell>{r.effectiveDate?.slice(0, 10)}</TableCell>
                        <TableCell><Typography variant="caption">{r.createdByName}</Typography></TableCell>
                        {isHR && (
                          <TableCell>
                            {!r.isReversed ? (
                              <Tooltip title="Reverse adjustment">
                                <IconButton size="small" onClick={() => reverse(r.id)}><Undo fontSize="small" /></IconButton>
                              </Tooltip>
                            ) : (
                              <Chip size="small" label="Reversed" sx={{ fontSize: '0.6rem', bgcolor: '#f5f5f5' }} />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Balance Admin ── */}
          {tab === 1 && isHR && (
            <>
              {filteredBal.filter(r => r.closingBalanceDays < 0).length > 0 && (
                <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                  {filteredBal.filter(r => r.closingBalanceDays < 0).length} employee-leave-type combinations have <strong>negative balances</strong> — review and correct.
                </Alert>
              )}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox size="small"
                          checked={selectedEmpIds.length > 0 && selectedEmpIds.length === filteredBal.map(r => r.employeeId).filter((v, i, a) => a.indexOf(v) === i).length}
                          onChange={e => setSelectedEmpIds(e.target.checked ? filteredBal.map(r => r.employeeId).filter((v, i, a) => a.indexOf(v) === i) : [])} />
                      </TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Leave Type</TableCell>
                      <TableCell align="right">Opening</TableCell>
                      <TableCell align="right">Accrued</TableCell>
                      <TableCell align="right">Carry Over</TableCell>
                      <TableCell align="right">Adjusted</TableCell>
                      <TableCell align="right">Used</TableCell>
                      <TableCell align="right">Closing</TableCell>
                      <TableCell align="right">Adj Count</TableCell>
                      <TableCell>Adjust</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBal.length === 0 && (
                      <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>No balances for {year}</TableCell></TableRow>
                    )}
                    {filteredBal.map(r => {
                      const closing = parseFloat(r.closingBalanceDays as any);
                      const adj     = parseFloat(r.adjustedDays as any);
                      return (
                        <TableRow key={`${r.employeeId}-${r.leaveTypeId}`} hover
                          sx={{ bgcolor: closing < 0 ? '#ffebee20' : undefined }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={selectedEmpIds.includes(r.employeeId)}
                              onChange={e => setSelectedEmpIds(p => e.target.checked ? [...p, r.employeeId] : p.filter(id => id !== r.employeeId))} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{r.firstName} {r.lastName}</Typography>
                            <Typography variant="caption" color="text.secondary">{r.deptName}</Typography>
                          </TableCell>
                          <TableCell><Chip size="small" label={r.leaveTypeName} sx={{ fontSize: '0.65rem' }} /></TableCell>
                          <TableCell align="right">{parseFloat(r.openingBalanceDays as any).toFixed(1)}</TableCell>
                          <TableCell align="right">{parseFloat(r.accruedDays as any).toFixed(1)}</TableCell>
                          <TableCell align="right">{parseFloat(r.carryOverDays as any).toFixed(1)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}
                              color={adj > 0 ? '#2e7d32' : adj < 0 ? '#b71c1c' : 'inherit'}>
                              {adj > 0 ? '+' : ''}{adj.toFixed(1)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{parseFloat(r.usedDays as any).toFixed(1)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700}
                              color={closing < 0 ? '#b71c1c' : closing === 0 ? '#546e7a' : '#2e7d32'}>
                              {closing.toFixed(1)}d
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {r.adjustmentCount > 0 ? <Chip size="small" label={r.adjustmentCount} sx={{ fontSize: '0.65rem' }} /> : '—'}
                          </TableCell>
                          <TableCell>
                            <Button size="small" onClick={() => {
                              setFormData({ ...emptyForm, employeeId: r.employeeId, leaveTypeId: r.leaveTypeId, year: r.year });
                              setFormOpen(true);
                            }}>Adjust</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedEmpIds.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{selectedEmpIds.length} selected</Typography>
                  <Button size="small" variant="contained" onClick={() => setBulkOpen(true)}
                    sx={{ bgcolor: '#C08A2E' }}>Bulk Grant Selected</Button>
                  <Button size="small" onClick={() => setSelectedEmpIds([])}>Clear</Button>
                </Box>
              )}
            </>
          )}

          {/* ── Dashboard ── */}
          {tab === 2 && isHR && stats?.byType && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>By Adjustment Type</Typography>
                  {stats.byType.map((t: any) => {
                    const cfg = ADJ_TYPE_CFG[t.adjustmentType] ?? ADJ_TYPE_CFG.other;
                    const maxCount = Math.max(...stats.byType.map((x: any) => parseInt(x.count)), 1);
                    return (
                      <Box key={t.adjustmentType} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="body2">{cfg.emoji} {cfg.label}</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">{t.count} adjustments</Typography>
                            <Typography variant="caption" fontWeight={700}
                              color={parseFloat(t.totalDays) >= 0 ? '#2e7d32' : '#b71c1c'}>
                              {parseFloat(t.totalDays) >= 0 ? '+' : ''}{parseFloat(t.totalDays).toFixed(1)}d
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.round((parseInt(t.count) / maxCount) * 100)}
                          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }} />
                      </Box>
                    );
                  })}
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Single Adjustment Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Leave Balance</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField select fullWidth label="Employee" value={formData.employeeId}
            onChange={e => setFormData(p => ({ ...p, employeeId: e.target.value }))} required>
            {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName} — {e.department?.name}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Leave Type" value={formData.leaveTypeId}
              onChange={e => setFormData(p => ({ ...p, leaveTypeId: e.target.value }))} required>
              {leaveTypes.map(lt => <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>)}
            </TextField>
            <TextField select label="Year" value={formData.year} onChange={e => setFormData(p => ({ ...p, year: parseInt(e.target.value) }))} sx={{ width: 100 }}>
              {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField fullWidth label="Days (+ to grant, − to deduct)" type="number"
              value={formData.days} onChange={e => setFormData(p => ({ ...p, days: parseFloat(e.target.value) || 0 }))}
              inputProps={{ step: 0.5 }} required
              helperText="Use negative values to deduct days" />
            <TextField select fullWidth label="Type" value={formData.adjustmentType}
              onChange={e => setFormData(p => ({ ...p, adjustmentType: e.target.value }))}>
              {Object.entries(ADJ_TYPE_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>)}
            </TextField>
          </Box>
          <TextField fullWidth label="Effective Date" type="date" InputLabelProps={{ shrink: true }}
            value={formData.effectiveDate} onChange={e => setFormData(p => ({ ...p, effectiveDate: e.target.value }))} />
          <TextField fullWidth label="Reason" value={formData.reason}
            onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))} required
            placeholder="e.g. Birthday leave bonus, Data correction, Long service award" />
          <TextField fullWidth label="Notes (optional)" multiline rows={2} value={formData.notes}
            onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || !formData.employeeId || !formData.leaveTypeId || !formData.reason}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#9E6F22' } }}>
            {saving ? 'Saving…' : 'Apply Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Grant Dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bulk Leave Grant
          {selectedEmpIds.length > 0 && <Chip size="small" label={`${selectedEmpIds.length} employees`} sx={{ ml: 1, bgcolor: '#C08A2E', color: '#fff' }} />}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {selectedEmpIds.length === 0 && (
            <Alert severity="info">Select employees from the Balance Admin tab first, or this will apply to all employees.</Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Leave Type" value={bulkData.leaveTypeId}
              onChange={e => setBulkData(p => ({ ...p, leaveTypeId: e.target.value }))} required>
              {leaveTypes.map(lt => <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>)}
            </TextField>
            <TextField select label="Year" value={bulkData.year} onChange={e => setBulkData(p => ({ ...p, year: parseInt(e.target.value) }))} sx={{ width: 100 }}>
              {[YEAR - 1, YEAR, YEAR + 1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Days to Grant" type="number" value={bulkData.days}
              onChange={e => setBulkData(p => ({ ...p, days: parseFloat(e.target.value) || 0 }))}
              inputProps={{ step: 0.5 }} required />
            <TextField select fullWidth label="Type" value={bulkData.adjustmentType}
              onChange={e => setBulkData(p => ({ ...p, adjustmentType: e.target.value }))}>
              {Object.entries(ADJ_TYPE_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>)}
            </TextField>
          </Box>
          <TextField fullWidth label="Reason" value={bulkData.reason}
            onChange={e => setBulkData(p => ({ ...p, reason: e.target.value }))} required />
          <TextField fullWidth label="Notes (optional)" multiline rows={2} value={bulkData.notes}
            onChange={e => setBulkData(p => ({ ...p, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBulk}
            disabled={saving || !bulkData.leaveTypeId || !bulkData.reason || selectedEmpIds.length === 0}
            sx={{ bgcolor: '#C08A2E' }}>
            {saving ? 'Applying…' : `Grant to ${selectedEmpIds.length} Employees`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
